import React from 'react';
import { Box, Link } from '@mui/material';
import KeyValueList, { KeyValueItem } from '../ui/KeyValueList';
import { formatDate } from '../../utils/format';
import { formatPriorityLabel } from '../../utils/projectStatus';
import type { ProjectData } from './types';
import { isMulticamSource } from './reviewModel';
import { recordingPhrase } from '../Multicam/separateAudio';

interface BriefPanelProps {
  project: ProjectData;
  editorName?: string | null;
}

const text = (value?: string | null) => (typeof value === 'string' && value.trim() ? value.trim() : null);

const multiline = (value: string) => <Box component="span" sx={{ whiteSpace: 'pre-wrap' }}>{value}</Box>;

/**
 * What the creator asked for, as label/value rows. Optional fields only
 * appear when they were filled in; the description always shows.
 */
const BriefPanel: React.FC<BriefPanelProps> = ({ project, editorName }) => {
  const multicam = isMulticamSource(project.sourceType) ? project.multicamSnapshot : null;
  const angles = multicam?.angles || [];
  const audioAngle = angles.find((angle) => angle.angleKey === multicam?.audioAngleKey);
  const deliverables = Array.isArray(project.deliverables)
    ? project.deliverables.filter(Boolean)
    : text(project.deliverables as string | null)
      ? [String(project.deliverables)]
      : [];
  const references = (project.referenceLinks || []).filter((link) => typeof link === 'string' && link.trim());

  const optional: Array<KeyValueItem | null> = [
    text(project.comments) ? { label: 'Notes for your editor', value: multiline(text(project.comments) as string), fullWidth: true } : null,
    text(project.targetAudience) ? { label: 'Audience', value: text(project.targetAudience) } : null,
    text(project.desiredStyle) ? { label: 'Style', value: text(project.desiredStyle) } : null,
    text(project.musicPreference) ? { label: 'Music', value: text(project.musicPreference) } : null,
    text(project.colorPreference) ? { label: 'Colour', value: text(project.colorPreference) } : null,
    text(project.brandGuidelines) ? { label: 'Brand guidelines', value: multiline(text(project.brandGuidelines) as string), fullWidth: true } : null,
    deliverables.length > 0 ? { label: 'Deliverables', value: deliverables.join(' · '), fullWidth: true } : null,
    text(project.timing) ? { label: 'Timing', value: multiline(text(project.timing) as string) } : null,
    text(project.textInstructions) ? { label: 'On-screen text', value: multiline(text(project.textInstructions) as string) } : null,
    text(project.overlay) ? { label: 'Overlays', value: multiline(text(project.overlay) as string) } : null,
    references.length > 0
      ? {
          label: 'References',
          fullWidth: true,
          value: (
            <Box component="span" sx={{ display: 'grid', gap: 0.25 }}>
              {references.map((link) => (
                <Link key={link} href={link} target="_blank" rel="noopener noreferrer" sx={{ overflowWrap: 'anywhere' }}>
                  {link}
                </Link>
              ))}
            </Box>
          ),
        }
      : null,
  ];

  const items: KeyValueItem[] = [
    { label: 'Description', value: text(project.description) ? multiline(text(project.description) as string) : null, fullWidth: true },
    ...optional.filter((item): item is KeyValueItem => Boolean(item)),
    {
      label: multicam ? 'Footage (multicam)' : 'Footage',
      value: multicam
        ? [
            multicam.name || 'Multicam set',
            `${angles.length} angle${angles.length === 1 ? '' : 's'}`,
            multicam.audioSource?.videoId
              ? `sound from ${recordingPhrase(multicam.audioSource)}`
              : audioAngle ? `sound from ${audioAngle.label || `angle ${audioAngle.angleKey}`}` : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : text(project.videoName),
    },
    {
      label: 'Editor',
      value: editorName ? [editorName, text(project.editorEmail || project.editor?.email)].filter(Boolean).join(' · ') : 'Not assigned yet',
    },
    ...(text(project.deadline) ? [{ label: 'Due', value: formatDate(project.deadline) }] : []),
    ...(text(project.priority) ? [{ label: 'Priority', value: formatPriorityLabel(project.priority) }] : []),
    { label: 'Created', value: project.createdAt ? formatDate(project.createdAt) : null },
    { label: 'Last updated', value: project.updatedAt ? formatDate(project.updatedAt) : null },
  ];

  return <KeyValueList items={items} />;
};

export default BriefPanel;
